import projectDropdownArrow from '../../../assets/project-dropdown-arrow.svg';
import { pxToRem } from '../../../utils/getFontValue';
import { RootState, useAppSelector } from '../../../redux/store';
import { UIColors } from '../../../constants/UIConstants';
import {
  Box,
  Icon,
  IconButton,
  MenuItem,
  Select,
  styled,
  Tooltip,
  Typography,
} from '@mui/material';
import { ArrowLeft } from 'react-feather';
import { useCallback, useEffect, useRef, useState, type UIEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { pageRoutes } from '../../../constants/routes';
import { listProjectRuns, type ProjectRunRow } from '../../../services/projectRunsApi';
import { formatOrgProjectScopeLabel } from '../../../types/onboarding';

const RUN_PAGE_SIZE = 25;

const StyledRunSelect = styled(Select)((props) => ({
  minWidth: pxToRem(280),
  height: pxToRem(48),
  color: UIColors.primary.navyBlue,
  '& .MuiTypography-root': {
    fontSize: pxToRem(18),
    fontWeight: 600,
    paddingLeft: pxToRem(8),
  },
}));

const StyledDropdownIcon = styled(Icon)({
  borderRadius: pxToRem(4),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

function RunScopeBar() {
  const navigate = useNavigate();
  const projectId = useAppSelector(
    (s: RootState) => s.userProject.currentProject.project_id
  );
  const currentRunId = useAppSelector(
    (s: RootState) => s.userProject.currentRunId
  );
  const selectedProject = useAppSelector(
    (s: RootState) => s.userProject.currentProject
  );

  const [runs, setRuns] = useState<ProjectRunRow[]>([]);
  const [hasMoreRuns, setHasMoreRuns] = useState(false);
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [loadingMoreRuns, setLoadingMoreRuns] = useState(false);
  const loadingMoreRunsRef = useRef(false);
  const hasMoreRunsRef = useRef(false);
  const runsPageRef = useRef(0);
  const keepMenuOpenRef = useRef(false);
  const [selectOpen, setSelectOpen] = useState(false);

  const loadRuns = useCallback(
    async (page = 0, replace = true) => {
      if (projectId == null || projectId <= 0) return;
      if (replace) setLoadingRuns(true);
      else setLoadingMoreRuns(true);

      try {
        const res = await listProjectRuns(projectId, {
          pageNumber: page,
          pageSize: RUN_PAGE_SIZE,
        });
        const list = res.runs ?? [];
        setRuns((prev) => {
          if (replace) return list;
          const seen = new Set(prev.map((run) => run.run_id));
          return [
            ...prev,
            ...list.filter((run) => !seen.has(run.run_id)),
          ];
        });
        runsPageRef.current = page;
        const more = res.has_more ?? false;
        setHasMoreRuns(more);
        hasMoreRunsRef.current = more;
      } catch {
        if (replace) setRuns([]);
        setHasMoreRuns(false);
        hasMoreRunsRef.current = false;
      } finally {
        if (replace) setLoadingRuns(false);
        else setLoadingMoreRuns(false);
        loadingMoreRunsRef.current = false;
      }
    },
    [projectId]
  );

  const loadMoreRuns = useCallback(() => {
    if (
      loadingMoreRunsRef.current ||
      !hasMoreRunsRef.current ||
      projectId == null ||
      projectId <= 0
    ) {
      return;
    }
    loadingMoreRunsRef.current = true;
    void loadRuns(runsPageRef.current + 1, false);
  }, [loadRuns, projectId]);

  useEffect(() => {
    setRuns([]);
    runsPageRef.current = 0;
    setHasMoreRuns(false);
    hasMoreRunsRef.current = false;
    void loadRuns(0, true);
  }, [loadRuns]);

  const handleMenuScroll = (event: UIEvent<HTMLUListElement>) => {
    const target = event.currentTarget;
    if (target.scrollTop + target.clientHeight < target.scrollHeight - 48) {
      return;
    }
    loadMoreRuns();
  };

  const handleRunChange = (event: { target: { value: unknown } }) => {
    const raw = event.target.value;
    if (raw === '__load_more__' || raw === '__loading_more__') return;
    const runId = Number(raw);
    if (!Number.isFinite(runId) || runId <= 0 || projectId <= 0) return;
    setSelectOpen(false);
    navigate(pageRoutes.dashboardProjectRun(projectId, runId));
  };

  const handleSelectClose = () => {
    if (keepMenuOpenRef.current) {
      keepMenuOpenRef.current = false;
      return;
    }
    setSelectOpen(false);
  };

  const handleBackToProjects = () => {
    navigate(pageRoutes.onboardingProjects);
  };

  const DropdownIcon = (props: Record<string, unknown>) => (
    <StyledDropdownIcon {...props}>
      <img alt="" src={projectDropdownArrow} />
    </StyledDropdownIcon>
  );

  const runIds = new Set(runs.map((r) => r.run_id));
  const showSyntheticCurrent =
    currentRunId != null &&
    currentRunId > 0 &&
    !runIds.has(currentRunId) &&
    !loadingRuns;

  const projectTitle = formatOrgProjectScopeLabel(
    selectedProject.client_org_name,
    selectedProject.project_name,
    { separator: ' — ', fallback: 'Project' }
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 1.5,
        minWidth: 0,
      }}
    >
      <Tooltip title="View all projects">
        <IconButton
          onClick={handleBackToProjects}
          size="small"
          aria-label="Back to projects"
          sx={{ color: UIColors.primary.navyBlue }}
        >
          <ArrowLeft size={22} />
        </IconButton>
      </Tooltip>

      <Typography
        component="span"
        title={projectTitle.truncated ? projectTitle.full : undefined}
        sx={{
          maxWidth: { xs: '100%', sm: pxToRem(360) },
          fontWeight: 700,
          fontSize: pxToRem(22),
          color: UIColors.primary.navyBlue,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {projectTitle.display}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <StyledRunSelect
          IconComponent={DropdownIcon}
          open={selectOpen}
          onOpen={() => setSelectOpen(true)}
          onClose={handleSelectClose}
          value={currentRunId != null && currentRunId > 0 ? currentRunId : ''}
          variant="standard"
          onChange={handleRunChange}
          displayEmpty
          disabled={(loadingRuns && runs.length === 0) || projectId <= 0}
          renderValue={(v) => {
            if (v === '' || v == null) {
              return (
                <Typography color="text.secondary">Select a run</Typography>
              );
            }
            const run = runs.find((r) => r.run_id === v);
            if (!run) {
              return (
                <Typography color="text.secondary" component="span">
                  {loadingRuns ? 'Loading…' : 'Run'}
                </Typography>
              );
            }
            return (
              <Typography component="span">
                Run {run.run_number ?? run.run_id}
              </Typography>
            );
          }}
          MenuProps={{
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
            MenuListProps: {
              onScroll: handleMenuScroll,
            },
            PaperProps: {
              sx: {
                maxHeight: pxToRem(320),
                color: UIColors.primary.navyBlue,
                '& .run-scope-load-more': {
                  justifyContent: 'center',
                  fontWeight: 600,
                },
              },
            },
          }}
        >
          {showSyntheticCurrent && (
            <MenuItem value={currentRunId}>
              <Typography component="span">Run (current)</Typography>
            </MenuItem>
          )}
          {runs.map((run) => (
            <MenuItem key={run.run_id} value={run.run_id}>
              <Typography component="span">
                Run {run.run_number ?? run.run_id}
                {' · '}
                {run.status.replace('_', ' ')}
              </Typography>
            </MenuItem>
          ))}
          {loadingMoreRuns && (
            <MenuItem disabled value="__loading_more__" className="run-scope-load-more">
              <Typography component="span">Loading more runs…</Typography>
            </MenuItem>
          )}
          {!loadingMoreRuns && hasMoreRuns && runs.length > 0 && (
            <MenuItem
              value="__load_more__"
              className="run-scope-load-more"
              onMouseDown={(event) => {
                event.preventDefault();
                keepMenuOpenRef.current = true;
              }}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                loadMoreRuns();
              }}
            >
              <Typography component="span">Load more runs</Typography>
            </MenuItem>
          )}
        </StyledRunSelect>
      </Box>
    </Box>
  );
}

export default RunScopeBar;
